<template>
    <b-modal
        :id="modalId"
        title="배정 규칙 추가"
        size="lg"
        no-close-on-backdrop
        :no-close-on-esc="busy"
        :hide-header-close="busy"
        @show="onShow"
        @hide="onHide"
        @hidden="onHidden"
    >
        <p class="text-secondary small">
            신청자의 답변이 아래 조건과 맞으면 고른 평가지로 배정됩니다.
            질문은 이 프로젝트에 등록된 것만 고를 수 있어, 규칙이 없는 질문을 가리켜 배정이 실패하는 일을 막습니다.
            <strong>여러 규칙을 한 번에 추가</strong>할 수 있습니다.
        </p>

        <p-page-empty-component
            v-if="inputKeys.length === 0"
            compact
            text="먼저 신청 질문을 만들어야 배정 조건을 정할 수 있습니다."
        />

        <template v-else>
            <b-card
                v-for="(block, bIdx) in blocks"
                :key="bIdx"
                no-body
                class="mb-2 border"
            >
                <!-- 접힌 상태: 평가지 이름만 -->
                <div v-if="block.collapsed" class="d-flex align-items-center justify-content-between p-2">
                    <div class="text-truncate">
                        <b-badge variant="primary" class="mr-2">규칙 {{ bIdx + 1 }}</b-badge>
                        <strong>{{ campaignName(block.campaignId) }}</strong>
                        <span class="text-secondary small ml-2">{{ blockSummary(block) }}</span>
                    </div>
                    <div class="flex-shrink-0">
                        <b-button size="sm" variant="link" class="p-1" @click="expandBlock(bIdx)">펼치기</b-button>
                        <b-button size="sm" variant="link" class="text-danger p-1" @click="removeBlock(bIdx)">삭제</b-button>
                    </div>
                </div>

                <!-- 펼친 상태: 편집기 -->
                <div v-else class="p-3">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                        <b-badge variant="primary">규칙 {{ bIdx + 1 }}</b-badge>
                        <div>
                            <b-button
                                v-if="blockComplete(block)"
                                size="sm"
                                variant="outline-secondary"
                                class="mr-1"
                                @click="collapseBlock(bIdx)"
                            >
                                접기
                            </b-button>
                            <b-button
                                v-if="blocks.length > 1"
                                size="sm"
                                variant="outline-danger"
                                @click="removeBlock(bIdx)"
                            >
                                삭제
                            </b-button>
                        </div>
                    </div>

                    <b-form-group label="배정할 평가지" label-class="fw-500">
                        <b-form-select v-model="block.campaignId" :options="campaignOptions"/>
                    </b-form-group>

                    <b-form-group label="조건을 묶는 방식" label-class="fw-500">
                        <b-form-radio-group v-model="block.groupOperator">
                            <b-form-radio value="AND">모두 만족(그리고)</b-form-radio>
                            <b-form-radio value="OR">하나라도 만족(또는)</b-form-radio>
                        </b-form-radio-group>
                    </b-form-group>

                    <label class="fw-500">이럴 때 배정</label>
                    <div
                        v-for="(row, idx) in block.rows"
                        :key="idx"
                        class="condition-row d-flex align-items-start flex-wrap mb-2"
                    >
                        <b-form-select
                            v-model="row.key"
                            :options="questionOptions"
                            class="row-field mr-1 mb-1"
                            @change="onKeyChange(row)"
                        />
                        <!-- 연산자와 값을 같은 tick 에 함께 바꾼다(IN 은 배열, 그 외는 값 하나) — v-model 대신 setOperator -->
                        <b-form-select
                            :value="row.operator"
                            :options="operatorOptionsFor(row.key)"
                            class="row-op mr-1 mb-1"
                            @input="(op) => setOperator(row, op)"
                        />
                        <!-- 값: 선택지형 질문이면 보기(IN 은 체크박스로 여러 개), 숫자면 number, 글자면 text -->
                        <b-form-checkbox-group
                            v-if="isListKey(row.key) && row.operator === 'IN'"
                            v-model="row.value"
                            :options="choiceOptions(row.key)"
                            stacked
                            class="row-field mr-1 mb-1"
                        />
                        <b-form-select
                            v-else-if="isListKey(row.key)"
                            v-model="row.value"
                            :options="choiceOptions(row.key)"
                            class="row-field mr-1 mb-1"
                        />
                        <b-form-input
                            v-else-if="valueTypeOf(row.key) === 'int'"
                            v-model.number="row.value"
                            type="number"
                            placeholder="숫자"
                            class="row-field mr-1 mb-1"
                        />
                        <b-form-input
                            v-else
                            v-model="row.value"
                            type="text"
                            placeholder="값"
                            class="row-field mr-1 mb-1"
                        />
                        <b-button
                            v-if="block.rows.length > 1"
                            variant="outline-danger"
                            size="sm"
                            class="mb-1"
                            @click="removeRow(block, idx)"
                        >
                            삭제
                        </b-button>
                    </div>

                    <div class="mb-2">
                        <b-button size="sm" variant="outline-secondary" @click="addRow(block)">
                            조건 추가
                        </b-button>
                    </div>

                    <b-alert :variant="blockComplete(block) ? 'light' : 'secondary'" show class="border mb-0">
                        <span class="text-secondary small">미리보기: </span>{{ blockPreview(block) }}
                    </b-alert>
                </div>
            </b-card>

            <b-button size="sm" variant="outline-primary" class="mt-1" @click="addBlock">
                + 규칙 추가
            </b-button>
        </template>

        <template #modal-footer>
            <b-button variant="secondary" :disabled="busy" @click="$bvModal.hide(modalId)">취소</b-button>
            <b-button variant="primary" :disabled="!canSave || busy" @click="save">
                <b-spinner v-if="saving" small class="mr-1"/>
                모두 저장하고 배정 켜기
            </b-button>
        </template>

        <campaign-rule-set-error-modal :error-data="errorData" @close="errorData = null"/>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import unsavedChangesGuard from '@shared/mixin/unsaved-changes-guard';
import {
    createCondition, createRuleGroup, createCampaignRuleSet,
    deleteCondition, deleteRuleGroup,
} from '@shared_utils/api/ruleEngine';
import {getCampaignOptions, getTargetCampaignText} from '@shared/components/admin/pages/Management/common-data';
import {operatorText} from './plain-language';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import CampaignRuleSetErrorModal from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetErrorModal.vue';

const OPS_BY_TYPE = {
    list: ['==', '!=', 'IN'],
    int: ['==', '!=', '<', '<=', '>', '>='],
    str: ['==', '!='],
};

export default {
    name: 'AssignmentRuleSentenceBuilder',
    components: {
        'p-page-empty-component': PageEmptyComponent,
        'campaign-rule-set-error-modal': CampaignRuleSetErrorModal,
    },
    mixins: [unsavedChangesGuard],
    props: {
        modalId: {type: String, default: 'assignment-rule-builder-modal'},
        projectId: {type: [Number, String], default: null},
        projectCode: {type: String, default: null},
        // 이 프로젝트에 등록된 신청 질문(input_keys). 질문 드롭다운은 이것만으로 채운다.
        inputKeys: {type: Array, default: () => []},
        campaigns: {type: Array, default: () => []},
    },
    data() {
        return {
            // 여러 규칙을 한 폼에서 만든다. 각 블록 = [조건들 → 평가지] 하나.
            blocks: [this.blankBlock()],
            saving: false,
            // 확인창 대기 중에도 닫기·중복저장을 막기 위한 상태.
            confirming: false,
            // 닫기 전 '저장하지 않은 입력' 확인창을 띄운 동안
            confirmingClose: false,
            errorData: null,
        };
    },
    computed: {
        campaignOptions() {
            return [{value: null, text: '평가지를 선택해주세요.'}].concat(
                getCampaignOptions(this.campaigns).map((o) => ({value: o.id, text: o.text}))
            );
        },
        questionOptions() {
            return [{value: null, text: '질문 선택', disabled: true}].concat(
                this.inputKeys.map((k) => ({value: k.key_name, text: this.labelOf(k)}))
            );
        },
        keyMap() {
            const m = {};
            this.inputKeys.forEach((k) => {
                m[k.key_name] = k;
            });
            return m;
        },
        // 확인창 대기(confirming) 또는 저장 중(saving)이면 모든 닫기/버튼을 잠근다.
        busy() {
            return this.saving || this.confirming;
        },
        // 평가지·질문·연산자·값 중 하나라도 고른 규칙이 있으면 닫기·새로고침 전에 묻는다(저장 중에도 새로고침은 막는다)
        hasUnsavedChanges() {
            return this.blocks.some((b) => !this.blockEmpty(b));
        },
        canSave() {
            // 완성된 블록이 하나 이상 있고, 어중간하게 채운(완성도 빈것도 아닌) 블록이 없어야 한다.
            // 완전히 빈 블록은 "아직 안 만든 것"으로 보고 무시한다.
            return this.blocks.some((b) => this.blockComplete(b))
                && this.blocks.every((b) => this.blockComplete(b) || this.blockEmpty(b));
        },
    },
    methods: {
        blankRow() {
            return {key: null, operator: null, value: null};
        },
        blankBlock() {
            return {campaignId: null, groupOperator: 'AND', rows: [this.blankRow()], collapsed: false};
        },
        onShow() {
            this.blocks = [this.blankBlock()];
            this.errorData = null;
            // saving/confirming 은 여기서 건드리지 않는다 — busy 중 닫기를 막으므로 재열기 시 항상 false.
        },
        // 확인창 대기·저장 중에는 닫히지 않게 막는다(경쟁상태 방지). @hide 는 preventDefault 가능.
        // 입력이 있으면 확인 후 닫는다(ESC·X·취소 모두).
        onHide(bvEvent) {
            if (this.busy || this.confirmingClose) {
                bvEvent.preventDefault();
                return;
            }
            if (!this.hasUnsavedChanges) return;
            bvEvent.preventDefault();
            this.confirmingClose = true;
            this.confirmDiscardChanges({title: '저장하지 않은 입력', message: '입력한 배정 규칙이 저장되지 않았습니다. 닫을까요?'}).then((ok) => {
                this.confirmingClose = false;
                if (!ok) return;
                this.blocks = [this.blankBlock()];
                this.$bvModal.hide(this.modalId);
            });
        },
        // 콘솔이 프로젝트를 바꾸기 전에(확인을 받은 뒤) 입력을 버리고 닫는다
        discardAndClose() {
            if (this.busy) return;
            this.blocks = [this.blankBlock()];
            this.$bvModal.hide(this.modalId);
        },
        onHidden() {
            // 저장 중 무효화 금지: 여기서 saving 을 건드리지 않는다(닫기는 이미 막힘).
            // 닫힌 뒤에는 남은 입력이 새로고침 경고를 띄우지 않게 비운다(다시 열면 onShow 도 비운다).
            this.blocks = [this.blankBlock()];
        },
        labelOf(k) {
            const lt = k.label_texts || {};
            return lt.ko || k.key_name;
        },
        valueTypeOf(key) {
            return this.keyMap[key] ? this.keyMap[key].value_type : 'str';
        },
        isListKey(key) {
            return this.valueTypeOf(key) === 'list';
        },
        operatorOptionsFor(key) {
            const type = this.valueTypeOf(key);
            const ops = OPS_BY_TYPE[type] || OPS_BY_TYPE.str;
            return [{value: null, text: '비교', disabled: true}].concat(
                ops.map((op) => ({value: op, text: this.opLabel(op)}))
            );
        },
        opLabel(op) {
            return `${operatorText(op)}${op === 'IN' ? '' : ` (${op})`}`;
        },
        choiceOptions(key) {
            const k = this.keyMap[key];
            const avs = (k && k.allowed_values) || [];
            return avs.map((av) => {
                const lt = av.label_texts || {};
                return {value: av.actual_value, text: lt.ko || av.actual_value};
            });
        },
        // 질문을 바꾸면 연산자/값을 초기화(타입이 달라져 잘못된 조합 방지).
        onKeyChange(row) {
            row.operator = null;
            row.value = null;
        },
        // IN 으로 바꾸면 고른 값 하나를 목록으로, IN 에서 벗어나면 목록의 첫 값만 남긴다(0·false 도 값)
        setOperator(row, op) {
            if (row.operator === op) return;
            const {value} = row;
            if (op === 'IN') {
                row.value = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);
            }
            else if (Array.isArray(value)) {
                row.value = value.length ? value[0] : null;
            }
            row.operator = op;
        },
        addRow(block) {
            block.rows.push(this.blankRow());
        },
        removeRow(block, idx) {
            block.rows.splice(idx, 1);
        },
        rowComplete(r) {
            if (!r.key || !r.operator) return false;
            if (r.operator === 'IN') return Array.isArray(r.value) && r.value.length > 0;
            // IN 이 아닌데 목록이 남아 있으면 미완성 — 목록이 '같음' 조건으로 저장되지 않게
            return r.value !== null && r.value !== '' && !Array.isArray(r.value);
        },
        rowText(r) {
            const label = this.keyMap[r.key] ? this.labelOf(this.keyMap[r.key]) : r.key;
            let val = r.value;
            if (this.isListKey(r.key)) {
                const opts = this.choiceOptions(r.key);
                const toText = (v) => {
                    const found = opts.find((o) => o.value === v);
                    return found ? found.text : v;
                };
                val = Array.isArray(r.value) ? r.value.map(toText).join(', ') : toText(r.value);
            }
            return `${label} ${operatorText(r.operator)} ${val}`;
        },
        // 백엔드로 보낼 조건값: 숫자 질문은 number, 그 외는 원본(선택지 actual_value / IN 배열 / 문자열).
        conditionValue(r) {
            if (this.valueTypeOf(r.key) === 'int' && r.operator !== 'IN') {
                return Number(r.value);
            }
            return r.value;
        },
        // ── 블록(규칙) 단위 헬퍼 ──────────────────────────────
        blockComplete(block) {
            return block.campaignId != null && block.rows.every((r) => this.rowComplete(r));
        },
        blockEmpty(block) {
            if (block.campaignId != null) return false;
            return block.rows.every((r) => !r.key && !r.operator
                && (r.value === null || r.value === '' || (Array.isArray(r.value) && r.value.length === 0)));
        },
        campaignName(id) {
            if (id == null) return '평가지 미선택';
            return getTargetCampaignText(id, this.campaigns) || `평가지 #${id}`;
        },
        blockSummary(block) {
            const n = block.rows.filter((r) => this.rowComplete(r)).length;
            return `조건 ${n}개`;
        },
        blockPreview(block) {
            if (block.campaignId == null) return '평가지와 조건을 채워주세요.';
            const parts = block.rows.filter((r) => this.rowComplete(r)).map((r) => this.rowText(r));
            if (parts.length === 0) return '조건을 채워주세요.';
            const conn = block.groupOperator === 'OR' ? ' 또는 ' : ' 그리고 ';
            const cname = getTargetCampaignText(block.campaignId, this.campaigns) || `평가지 #${block.campaignId}`;
            return `${parts.join(conn)} → ${cname} 배정`;
        },
        addBlock() {
            // "추가하고 완료된 건 접어서" — 새 규칙을 열 때 이미 완성된 블록은 접는다.
            this.blocks.forEach((b) => {
                if (this.blockComplete(b)) b.collapsed = true;
            });
            this.blocks.push(this.blankBlock());
        },
        removeBlock(idx) {
            this.blocks.splice(idx, 1);
            if (this.blocks.length === 0) this.blocks.push(this.blankBlock());
        },
        collapseBlock(idx) {
            this.blocks[idx].collapsed = true;
        },
        expandBlock(idx) {
            this.blocks[idx].collapsed = false;
        },
        snapshotBlock(block) {
            return {
                projectId: this.projectId,
                projectCode: this.projectCode,
                campaignId: block.campaignId,
                cname: getTargetCampaignText(block.campaignId, this.campaigns) || `평가지 #${block.campaignId}`,
                groupOperator: block.groupOperator,
                rows: JSON.parse(JSON.stringify(block.rows)),
                rowTexts: block.rows.map((r) => this.rowText(r)),
                rowValues: block.rows.map((r) => this.conditionValue(r)),
            };
        },
        async save() {
            if (!this.canSave || this.busy) return;

            // (#1/#3) 클릭 즉시 confirming 을 켜고 **불변 스냅샷**을 확보한다. 이후 확인창 대기 중에도
            // 닫기·중복저장이 막히고(busy), 생성은 오직 이 스냅샷만 사용한다.
            this.confirming = true;
            // 저장 대상 = 완성된 블록만(빈 블록 무시). 블록 참조와 스냅샷을 함께 보관.
            const targets = this.blocks
                .filter((b) => this.blockComplete(b))
                .map((b) => ({block: b, snap: this.snapshotBlock(b)}));
            if (targets.length === 0) {
                this.confirming = false;
                return;
            }

            // (#2) 런타임 계약: 활성 규칙이 있으면 미매칭 신청자는 기본 평가지로 폴백되지 않고
            // campaign_id:null 로 신청이 실패할 수 있다. 폴백을 안전한 것처럼 안내하지 않는다.
            const coverageMsg = `${targets.length}개의 배정 규칙을 켜서 저장합니다. `
                + '각 규칙의 조건에 맞지 않는 신청자는 그 규칙으로 배정되지 않습니다. '
                + '어떤 규칙에도 맞지 않으면 배정될 평가지가 없어 신청이 실패할 수 있습니다. 그래도 저장할까요?';
            let confirmed = false;
            try {
                confirmed = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: '배정 규칙을 켜서 저장',
                    message: coverageMsg,
                    modalOptions: {okTitle: '켜서 저장'},
                });
            }
            catch (e) {
                confirmed = false;
            }
            if (!confirmed) {
                this.confirming = false;
                return;
            }

            // 확인 완료 → 저장 시작(confirming→saving 로 잠금 이관).
            this.saving = true;
            this.confirming = false;

            // 규칙은 서로 독립적이다. 순서대로 저장하고, 하나가 실패하면 멈춘다.
            // 성공한 규칙은 이미 커밋된 유효 규칙이므로 되돌리지 않고, 폼에서 제거한다.
            const savedBlocks = [];
            let failure = null;
            for (const t of targets) {
                // eslint-disable-next-line no-await-in-loop
                const r = await this.saveOneBlock(t.snap);
                if (r.ok) {
                    savedBlocks.push(t.block);
                }
                else {
                    failure = {e: r.e, conditionIds: r.conditionIds, groupId: r.groupId};
                    break;
                }
            }

            this.saving = false;

            if (savedBlocks.length > 0) {
                this.blocks = this.blocks.filter((b) => !savedBlocks.includes(b));
                if (this.blocks.length === 0) this.blocks = [this.blankBlock()];
                this.$emit('created');
            }

            if (failure) {
                if (savedBlocks.length > 0) {
                    this.$EventBus.$emit('toast-success', '배정 규칙 추가',
                        `${savedBlocks.length}개는 저장했고, 다음 규칙에서 멈췄습니다. 남은 규칙을 확인해주세요.`);
                }
                await this.handleSaveError(failure.e, failure.conditionIds, failure.groupId);
                // 모달은 열어둔다(실패·미완료 블록을 남겨 재시도).
            }
            else {
                this.$EventBus.$emit('toast-success', '배정 규칙 추가',
                    `${savedBlocks.length}개 배정 규칙을 만들었습니다.`);
                this.$bvModal.hide(this.modalId);
            }
        },
        // 규칙 하나 저장(조건→그룹→규칙셋). 성공 {ok:true} / 실패 {ok:false, e, conditionIds, groupId}.
        // (#3/red) 보상 삭제는 handleSaveError 에서 커밋 전 확실 거절(400/409)일 때만 한다.
        async saveOneBlock(snap) {
            const createdConditionIds = [];
            let createdGroupId = null;
            try {
                for (let i = 0; i < snap.rows.length; i += 1) {
                    const r = snap.rows[i];
                    // eslint-disable-next-line no-await-in-loop
                    const res = await createCondition({
                        name: snap.rowTexts[i], key: r.key, operator: r.operator, value: snap.rowValues[i],
                    });
                    createdConditionIds.push(res.data.id);
                }
                const groupRes = await createRuleGroup({
                    name: `${snap.cname} 배정 조건`, operator: snap.groupOperator, description: '',
                    condition_ids: createdConditionIds, child_group_ids: [],
                });
                createdGroupId = groupRes.data.id;
                await createCampaignRuleSet({
                    name: snap.projectCode ? `${snap.projectCode} - ${snap.cname}` : snap.cname,
                    description: '', is_active: true,
                    project_id: snap.projectId, campaign_id: snap.campaignId,
                    root_rule_group_id: createdGroupId,
                });
                return {ok: true};
            }
            catch (e) {
                return {ok: false, e, conditionIds: createdConditionIds, groupId: createdGroupId};
            }
        },
        async handleSaveError(e, conditionIds, groupId) {
            const status = e && e.response && e.response.status;
            // 보상 삭제는 서버가 **커밋 전에 거절**했음이 확실한 400/409 일 때만 한다(단계 무관).
            // 그 외(500·502/503/504·응답 유실)는 커밋 여부가 불확실하므로 삭제하지 않고 사용자에게 안내한다.
            const definitivePreCommitReject = status === 400 || status === 409;
            let cleaned = true;
            if (definitivePreCommitReject) {
                cleaned = await this.compensate(conditionIds, groupId);
            }
            const tail = definitivePreCommitReject
                ? (cleaned ? '' : ' 일부 임시 데이터가 남았을 수 있어 고급 화면에서 확인이 필요합니다.')
                : ' 저장 결과가 확실하지 않습니다. 임시로 만든 데이터가 남았을 수 있으니 고급 화면에서 규칙이 만들어졌는지 확인해주세요.';

            if (status === 409) {
                this.errorData = e.response.data; // Z3 충돌(커밋 전 거절): 기존 충돌 모달 재사용
                this.$bvModal.show('campaign-rule-set-conflict-error-modal');
                if (tail) {
                    this.$EventBus.$emit('toast-failure', '규칙 충돌',
                        `충돌로 저장하지 못했습니다.${tail}`);
                }
            }
            else if (status === 400) {
                const msg = getErrorMessage(e, '규칙이 참조하는 질문이 신청서에 없습니다.');
                this.$EventBus.$emit('toast-failure', '저장 실패', `${msg}${tail}`);
            }
            else {
                const msg = getErrorMessage(e, '규칙 저장 중 서버 오류가 발생했습니다.');
                this.$EventBus.$emit('toast-failure', '저장 실패', `${msg}${tail}`);
            }
        },
        // 부분 실패 보상: 규칙셋이 확실히 안 생겼을 때만 호출. 그룹 먼저→조건 순(참조무결성).
        async compensate(conditionIds, groupId) {
            let ok = true;
            if (groupId != null) {
                try {
                    await deleteRuleGroup(groupId);
                }
                catch (e) {
                    ok = false;
                }
            }
            for (const id of conditionIds) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await deleteCondition(id);
                }
                catch (e) {
                    ok = false;
                }
            }
            return ok;
        },
    },
};
</script>

<style lang="scss" scoped>
.condition-row {
    gap: 0;
}

.row-field {
    flex: 1 1 180px;
    min-width: 140px;
}

.row-op {
    flex: 0 0 150px;
}
</style>
